import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from '@mui/material';
import Drawer from '../ReusableComponents/NewDesign/Layout/Drawer/Drawer';
import TopBar from '../ReusableComponents/NewDesign/Layout/TopBar/TopBar';
import { Container, Main } from '../ReusableComponents/NewDesign/Layout/Layout';
import BuildForm from '../ReusableComponents/BuildForm/BuildForm';
import styles from './VendorApplicationOnboarding.module.css';
import locationProps from '../../Lib/Utils/PropTypes/location-props';
import { ApplicationStatus } from '../../Lib/Constants/vendor-application-form-step';
import ActionButtonMode from '../../Lib/Constants/action-button-mode';

import {
  useBuyerSubmitApplication,
  useBuyerValidateStep,
  useChangeSponsorApproval,
  useFirstExecutiveApproval,
  useGetVendorOnboardingApplication,
  useGetVendorOnboardingSignedUrls,
  useSaveApplicationWithoutValidation,
  useSecondExecutiveApproval,
  useSponsorApproval,
  useVendorSubmitApplication,
  useVendorValidateStep,
} from '../../Lib/Hooks/vendor-onboarding.hooks.ts';
import {
  getStepList,
  isEditable,
  isFirstExecutiveApprovable,
  isLocked,
  isSecondExecutiveApprovable,
  isSponsorAprovable,
  isViewMode,
  staticComponents,
} from './StepsSelector';
import CustomModal from '../ReusableComponents/NewDesign/Modals/Modals';
import VendorApplicationBuyerEditModal from './VendorApplicationBuyerEditModal.tsx';

function VendorApplicationOnboarding({ currentUser, location, logout }) {
  const vendorId = location.pathname.split('/')[2];
  const [buyerEditModalOpen, setBuyerEditModalOpen] = useState(false);

  const [activeStep, setActiveStep] = useState(0);
  const [displayedStep, setDisplayedStep] = useState(0);
  const [formsData, setFormsData] = useState({});
  const [mode, setMode] = useState(ActionButtonMode.NONE);
  const saveChangeRef = useRef(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const applicationRef = useRef(null);
  const [steps, setSteps] = useState([]);
  const [vendorApplication, setVendorApplication] = useState({});
  const [isSponsorApprovalStep, setIsSponsorApprovalStep] = useState(false);
  const [isApprovalStep, setIsApprovalStep] = useState(false);

  const [isFirstExecutiveApprovalStep, setIsFirstExecutiveApprovalStep] =
    useState(false);
  const [isSecondExecutiveApprovalStep, setIsSecondExecutiveApprovalStep] =
    useState(false);

  const { mutate: sponsorApproval } = useSponsorApproval();
  const { mutate: changeSponsorApproval } = useChangeSponsorApproval();
  const { mutate: firstExecutiveApproval } = useFirstExecutiveApproval();
  const { mutate: secondExecutiveApproval } = useSecondExecutiveApproval();

  const getVendorOnboardingApplication =
    useGetVendorOnboardingApplication(vendorId);

  useEffect(() => {
    if (getVendorOnboardingApplication.data) {
      const application = getVendorOnboardingApplication.data;

      applicationRef.current = application;

      setVendorApplication(application);

      setSteps(
        getStepList(
          application.vendor_type,
          currentUser.role,
          application.status,
        ),
      );

      const { answers } = application;
      setFormsData((prevFormData) => ({
        ...prevFormData,
        ...answers.reduce(
          (acc, answer) => ({ ...acc, [answer.question]: answer.answer }),
          {},
        ),
      }));

      setIsSponsorApprovalStep(
        isSponsorAprovable(currentUser.role, application.status),
      );
      setIsFirstExecutiveApprovalStep(
        isFirstExecutiveApprovable(currentUser.role, application.status),
      );
      setIsSecondExecutiveApprovalStep(
        isSecondExecutiveApprovable(
          currentUser.role,
          application.status,
          currentUser.id,
          application.metadata,
        ),
      );
    }
  }, [getVendorOnboardingApplication.data]);

  const saveApplicationWithoutValidation =
    useSaveApplicationWithoutValidation();
  const vendorValidateStep = useVendorValidateStep();
  const buyerValidateStep = useBuyerValidateStep();
  const submitVendorApplication = useVendorSubmitApplication();
  const submitBuyerApplication = useBuyerSubmitApplication();

  const isLoading =
    getVendorOnboardingApplication.isLoading ||
    submitVendorApplication.isLoading ||
    submitBuyerApplication.isLoading ||
    vendorValidateStep.isLoading ||
    buyerValidateStep.isLoading;

  const getSignedUrls = useGetVendorOnboardingSignedUrls();

  const getUploadUrl = useCallback(
    async (fileName) => {
      const res = await getSignedUrls.mutateAsync({
        vendorId,
        fileName,
      });

      return res.uploadUrl;
    },
    [vendorId, getSignedUrls],
  );

  const getDownloadUrl = useCallback(
    async (fileName) => {
      const res = await getSignedUrls.mutateAsync({
        vendorId,
        fileName,
      });

      return res.downloadUrl;
    },
    [vendorId, getSignedUrls],
  );

  const handleFormSubmit = (stepIndex, stepData) => {
    const validateStep =
      applicationRef.current.status === ApplicationStatus.VENDOR_QUESTIONNAIRE
        ? vendorValidateStep
        : buyerValidateStep;

    validateStep.mutate(
      {
        answers: Object.entries(stepData).reduce((acc, [key, value]) => {
          if (value === undefined) {
            return acc;
          }

          return {
            ...acc,
            [key]: value,
          };
        }, {}),
        step: steps[stepIndex].label,
        id: applicationRef.current.id,
      },
      {
        onSuccess: () => {
          setFormsData({ ...formsData, ...stepData });
          if (stepIndex === steps.length - 1) {
            const appSubmit =
              applicationRef.current.status ===
              ApplicationStatus.VENDOR_QUESTIONNAIRE
                ? submitVendorApplication
                : submitBuyerApplication;

            appSubmit.mutate(
              { id: applicationRef.current.id },
              {
                onSuccess: () => {
                  getVendorOnboardingApplication.refetch();
                  setIsEditMode(false);
                },
              },
            );
            return;
          }
          if (stepIndex === activeStep) {
            setActiveStep(
              steps.findIndex((step) => step.label === steps[stepIndex].label) +
                1,
            );
          }
          setIsEditMode(false);
          setDisplayedStep(
            steps.findIndex((step) => step.label === steps[stepIndex].label) +
              1,
          );
        },
      },
    );
  };

  useEffect(() => {
    if (applicationRef.current) {
      if (
        isEditable(
          currentUser.role,
          applicationRef.current.status,
          displayedStep,
          activeStep,
        )
      ) {
        setMode(ActionButtonMode.EDIT);
      } else {
        setMode(ActionButtonMode.NONE);
      }
    }
  }, [displayedStep]);

  // set Is Approval Step
  useEffect(() => {
    if (applicationRef.current) {
      setIsApprovalStep(
        isSponsorApprovalStep ||
          isFirstExecutiveApprovalStep ||
          isSecondExecutiveApprovalStep,
      );
    }
  }, [
    isSponsorApprovalStep,
    isFirstExecutiveApprovalStep,
    isSecondExecutiveApprovalStep,
  ]);

  useEffect(() => {
    if (steps.length > 0 && applicationRef.current) {
      let currentStepIndex = steps.findIndex(
        (step) => step.label === applicationRef.current.current_step,
      );
      if (currentStepIndex === -1) {
        currentStepIndex = steps.length - 1;
      }
      setDisplayedStep(currentStepIndex);
      setActiveStep(currentStepIndex);
    }
  }, [steps]);

  const changeMode = () => {
    if (mode === ActionButtonMode.EDIT) {
      setIsEditMode(true);
      saveChangeRef.current = false;
    } else {
      setIsEditMode(false);
      saveChangeRef.current = true;
      setDisplayedStep(activeStep);
    }
  };

  const renderStepContent = (step, index) => {
    if (step.component && !step.static) {
      return (
        <div className={displayedStep === index ? '' : styles.hidden}>
          <BuildForm
            formPage={step.component}
            onSubmit={(stepData) => handleFormSubmit(index, stepData)}
            viewMode={isViewMode(
              activeStep,
              index,
              isEditMode,
              currentUser.role,
            )}
            buttonText={index === steps.length - 1 ? 'Submit' : 'Continue'}
            formsData={formsData}
            locked={
              activeStep < displayedStep ||
              isLocked(
                currentUser.role,
                applicationRef.current.status,
                step.label,
              ) ||
              isLoading
            }
            onFieldChange={(name, value) => {
              if (saveChangeRef.current) {
                saveApplicationWithoutValidation.mutate({
                  id: applicationRef.current.id,
                  step: step.label,
                  field: name,
                  value,
                });
              }
            }}
            level={0}
            getUploadUrl={getUploadUrl}
            getDownloadUrl={getDownloadUrl}
          />
        </div>
      );
    }
    return (
      <div
        className={
          displayedStep ===
          steps.findIndex((s) => staticComponents.includes(s.label))
            ? ''
            : styles.hidden
        }
      >
        <step.component />
      </div>
    );
  };

  const isSecondExecutiveApproved =
    applicationRef?.current?.metadata?.secondExecutiveApproval?.status ===
    'Approved';

  return (
    <Container>
      <TopBar
        currentUser={currentUser}
        currentApplication={vendorApplication}
        buttonText={mode}
        onClickButton={changeMode}
        editMode={isEditMode}
        approvalMode={isApprovalStep}
        onApprove={(approved) => {
          if (
            applicationRef.current.status === ApplicationStatus.SPONSOR_APPROVAL
          ) {
            sponsorApproval({
              id: applicationRef.current.id,
              approved: approved === 'approve',
            });
          } else if (
            applicationRef.current.status ===
            ApplicationStatus.FIRST_EXECUTIVE_APPROVAL
          ) {
            firstExecutiveApproval({
              id: applicationRef.current.id,
              approved: approved === 'approve',
            });
          } else if (
            applicationRef.current.status ===
            ApplicationStatus.SECOND_EXECUTIVE_APPROVAL
          ) {
            secondExecutiveApproval({
              id: applicationRef.current.id,
              approved: approved === 'approve',
            });
          } else if (
            applicationRef.current.status ===
              ApplicationStatus.SPONSOR_DENIED &&
            currentUser.role === 'EXECUTIVE'
          ) {
            changeSponsorApproval({
              id: applicationRef.current.id,
            });
          }
        }}
      />
      <CustomModal
        size="large"
        open={buyerEditModalOpen}
        handleClose={() => {
          setBuyerEditModalOpen(false);
        }}
      >
        <VendorApplicationBuyerEditModal
          currentUser={currentUser}
          vendorApplication={vendorApplication}
          formData={formsData}
          onClose={() => {
            setBuyerEditModalOpen(false);
          }}
        />
      </CustomModal>
      <Drawer
        currentUser={currentUser}
        vendorApplication={vendorApplication}
        editButton={
          (currentUser.role === 'BUYER' ||
            currentUser.role === 'PRODUCT_DEVELOPMENT') &&
          applicationRef.current &&
          applicationRef.current.application_archived_at === null &&
          [
            ApplicationStatus.INITIATED,
            ApplicationStatus.VENDOR_QUESTIONNAIRE,
            ApplicationStatus.BUYER_QUESTIONNAIRE,
          ].includes(applicationRef.current.status)
        }
        onEditButtonClick={() => {
          setBuyerEditModalOpen(true);
        }}
        stepper={{
          steps: steps.map((step) => step.label),
          activeStep,
          displayedStep,
          editMode: isEditMode,
          isSecondExecutiveApproved,
        }}
        onNavigationClick={(index) => {
          setDisplayedStep(index);
          window.scrollTo({ top: 0 });
        }}
        logout={logout}
      />
      {isLoading ? (
        <div className={styles.loaderContainer}>
          <CircularProgress />
        </div>
      ) : (
        <Main>
          {steps.map((step, index) => (
            <div key={step.label}>{renderStepContent(step, index)}</div>
          ))}
        </Main>
      )}
    </Container>
  );
}

VendorApplicationOnboarding.propTypes = {
  location: locationProps.isRequired,
  currentUser: PropTypes.shape({
    role: PropTypes.string,
  }).isRequired,
  logout: PropTypes.func.isRequired,
};

export default VendorApplicationOnboarding;
